// /pages/MonthlyInOutPage.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging,
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const MONTHLY_IN_OUT_API_URL = `${API_URL}/api/oracle/monthly-in-out`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;

export default function MonthlyInOutPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [custList, setCustList] = useState([]);
  
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const dataGridRef = useRef(null);

  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}`;
  };

  // CUST 목록 조회
  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } }).then(res => setCustList(res.data.data || []));
  }, [selectedFactory]);

  // 사용자 역할에 따른 CUST 자동 선택
  useEffect(() => {
    if (user?.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) setSelectedCust(found.CODE_CD);
    }
  }, [user, custList]);
  
  const fetchData = useCallback(() => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    const params = {
      factory: selectedFactory,
      startDate: formatDate(dateFrom),
      endDate: formatDate(dateTo),
      custCd: selectedCust,
    };
    
    axios.get(MONTHLY_IN_OUT_API_URL, { params })
      .then(res => {
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setData(res.data.data.map((row, idx) => ({ ...row, id: idx })));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.retMsg || e.message;
        setError(`조회 오류: ${errorMessage}`);
        notify(errorMessage, 'error', 3000);
      })
      .finally(() => setLoading(false));
  }, [selectedFactory, dateFrom, dateTo, selectedCust]);
  
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Monthly_In_Out");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Monthly_In_Out.xlsx"));
    e.cancel = true;
  };
  
  return (
    <div style={{ padding: 20, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>Monthly In/Out Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['FT01', 'PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} disabled={user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" width={220} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"></div>}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Sorting mode="multiple" />       
       

          {/* ★★★ [수정] 컬럼 정의 ★★★ */}
          <Column dataField="FACTORY" caption="Factory" width={100}  />
          <Column dataField="CUSTOMER" caption="Customer" width={120}  />
          <Column dataField="MAT_ID" caption="Device" width={150} />
          <Column dataField="LOT_ID" caption="Lot ID" width={150} />
          <Column dataField="IN_QTY" caption="In Qty" dataType="number" alignment="right" width={120} />
          <Column dataField="SHIP_QTY" caption="Ship Qty" dataType="number" alignment="right" width={120} />
          <Column dataField="LOSS_QTY" caption="Loss Qty" dataType="number" alignment="right" width={120} />
          <Column dataField="LIS_QTY" caption="LIS Loss" dataType="number" alignment="right" width={120} />
          <Column dataField="TEST_QTY" caption="Test Loss" dataType="number" alignment="right" width={120} />
          <Column dataField="CV_QTY" caption="CV Loss" dataType="number" alignment="right" width={120} />
          <Column dataField="EOH" caption="EOH" dataType="number" alignment="right" width={120} />

        </DataGrid>
      </div>
    </div>
  );
}